import React from "react";
import { ReferenceLine } from "recharts";
import { DEFAULT_COLORS } from "../mainUnit";
import { ChartBox, TooltipBox } from "./recharts.styles";

export { ChartBox } from "./recharts.styles";

export const PALETTE = DEFAULT_COLORS;
export const colorAt = (i) => PALETTE[i % PALETTE.length];

export const GRID    = "#e2e8f0";
export const AXIS    = "#94a3b8";
export const INK     = "#1e293b";
export const SUCCESS = "#3f8a5c";
export const DANGER  = "#c0392b";

export const num = (v) =>
  v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Compact axis tick: 1500 → "1.5k", 2000000 → "2M". */
export const compactAxis = (v) => {
  if (v == null || isNaN(v)) return "";
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${+(n / 1_000).toFixed(1)}k`;
  return `${n}`;
};

const AXIS_TICK = { fontSize: 12, fill: AXIS };
export const xAxisProps = { tick: AXIS_TICK, axisLine: { stroke: "#d3d1c7" }, tickLine: false, minTickGap: 20 };
export const yAxisProps = { tick: AXIS_TICK, axisLine: false, tickLine: false };

export const CHART_MARGIN = { top: 8, right: 60, left: 0, bottom: 0 };

export const EmptyState = ({ message = "No data available" }) => (
  <ChartBox
    style={{ display: "flex", alignItems: "center", justifyContent: "center", color: AXIS, fontSize: 13 }}
  >
    {message}
  </ChartBox>
);

/**
 * Default tooltip. Pass as <Tooltip content={<KitTooltip unit="tCO₂e" />} />;
 * recharts injects active/payload/label.
 * @param {string} [props.unit]
 * @param {(v:number)=>string} [props.formatValue]
 */
export const KitTooltip = ({ active, payload, label, unit, formatValue = num }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value != null && p.value !== "");
  if (!rows.length) return null;
  return (
    <TooltipBox>
      <div className="tt-hd">{label}</div>
      {rows.map((p) => (
        <div className="tt-row" key={p.dataKey}>
          <span className="tt-sw" style={{ background: p.color || p.stroke || p.fill }} />
          <span className="tt-nm">{p.name}</span>
          <span className="tt-val">
            {formatValue(p.value)}
            {unit ? <span className="tt-unit">{unit}</span> : null}
          </span>
        </div>
      ))}
    </TooltipBox>
  );
};

/**
 * Returns an array of <ReferenceLine> to spread directly into a chart (recharts
 * only recognises reference lines as direct children, not wrapped in a component).
 * @param {{x?:number,y?:number,label?:string,color?:string}[]} [refLines]
 */
export const renderRefLines = (refLines = []) =>
  refLines.map((r, i) => (
    <ReferenceLine
      key={`ref-${i}`}
      x={r.x}
      y={r.y}
      stroke={r.color || AXIS}
      strokeDasharray="4 4"
      label={r.label ? { value: r.label, position: "top", fontSize: 10, fill: "#64748b" } : undefined}
    />
  ));

/** Fills in name (← key) and color (← palette) so callers can pass minimal series. */
export const normaliseSeries = (series = []) =>
  series.map((s, i) => ({
    name: s.key,
    color: colorAt(i),
    ...s,
  }));
